import { forkJoin } from 'rxjs';
import { BaseForm } from 'projects/admin/src/app/classes/base-form';
import { Component, OnInit, Input, Output, EventEmitter } from '@angular/core';
import { AcModel } from 'projects/core/src/lib/models/ac.model';
import { UntypedFormGroup, UntypedFormBuilder, UntypedFormControl, Validators } from '@angular/forms';
import { MenuItem, SelectItem, ConfirmationService } from 'primeng/api';
import { UiService } from 'projects/admin/src/app/services/ui.service';
import { ApiService } from 'projects/admin/src/app/services/api.service';
import { ActivatedRoute, Router } from '@angular/router';
import { LANGUAGES } from 'projects/core/src/lib/constants';
import { RatingtypeModel } from 'projects/core/src/lib/models/ratingtype.model';
import { TranslocoService } from '@ngneat/transloco';
import { UserModel } from 'projects/core/src/lib/models/user.model';
import { HelperService } from 'projects/admin/src/app/services/helper.service';
import { AuthStateService } from 'projects/admin/src/app/services/auth-state.service';
import { FormHelper } from 'projects/admin/src/app/shared/form-helper';
import * as moment from 'moment';
import { CustomerModel } from 'projects/core/src/lib/models/customer.model';

@Component({
  selector: 'app-ac-basedata',
  templateUrl: './ac-basedata.component.html',
  styleUrls: ['./ac-basedata.component.scss']
})
export class AcBasedataComponent extends BaseForm implements OnInit {

  @Input() ac: AcModel = null;
  @Output() setEdit = new EventEmitter<string>();

  public customers: Array<CustomerModel> = [];
  public dispatchers: Array<UserModel> = [];
  public mode = 'planned';
  public country: string | null = null;
  public timezone: string | null = null;

  public locationTypes: Array<SelectItem> = [
    {label: this.transloco.translate('common.labels.onsite'), value: 'onsite'},
    {label: this.transloco.translate('common.labels.virtual'), value: 'virtual'},
  ];

  public form: UntypedFormGroup = this.fb.group({
    name: new UntypedFormControl('', [Validators.required]),
    ratingtype: new UntypedFormControl('', [Validators.required]),
    language: new UntypedFormControl('', [Validators.required]),
    customer: new UntypedFormControl(''),
    dispatcher: new UntypedFormControl('', [Validators.required]),
    location_type: new UntypedFormControl(''),
    location_name: new UntypedFormControl(''),
    conference_url: new UntypedFormControl(''),
    conference_info: new UntypedFormControl(''),
    informations_internal: new UntypedFormControl(''),
    informations_moderator: new UntypedFormControl(''),
    documents_visible_observer: new UntypedFormControl(''),
    start_time: new UntypedFormControl('', [Validators.required]),
    tz: this.fb.group({})
  });

  public items: Array<MenuItem> = [];
  public languages: Array<SelectItem> = [];
  public ratingtypes: Array<any> = [];

  constructor(
    private fb: UntypedFormBuilder,
    private ui: UiService,
    private api: ApiService,
    private auth: AuthStateService,
    private router: Router,
    private route: ActivatedRoute,
    private helper: HelperService,
    private confirmationService: ConfirmationService,
    public transloco: TranslocoService,
  ) {
    super();
  }

  ngOnInit(): void {
    this.languages = FormHelper.getSelectList(LANGUAGES, null, this.transloco);
    this.route.params.subscribe(params => {
      if (params.mode) {
        this.mode = params.mode;
      }
    });
    this.buildMenu();
    this.loading = true;
    forkJoin([
      this.api.getModels('ratingtypes'),
      this.api.getModels('users', {type: 'dispatcher'}),
      this.api.getModels('customers', {ac: this.ac.id}),
    ]).subscribe({
      next: (result: Array<any>) => {
        if (result[0] !== null && result[0].data.length > 0) {
          result[0].data.forEach((ratingtype: any) => {
            this.ratingtypes.push(new RatingtypeModel().deserialize(ratingtype));
          });
        }
        if (result[1] !== null && result[1].data.length > 0) {
          result[1].data.forEach((dispatcher: any) => {
            this.dispatchers.push(new UserModel().deserialize(dispatcher));
          });
        }
        this.dispatchers.sort((a: UserModel, b: UserModel) => {
          return (a.lastname > b.lastname) ? 1 : ((b.lastname > a.lastname) ? -1 : 0);
        });
        if (result[2] !== null && result[2].data.length > 0) {
          result[2].data.forEach((customer: any) => {
            this.customers.push(new CustomerModel().deserialize(customer));
          });
        }
        this.customers.sort((a: CustomerModel, b: CustomerModel) => {
          return this.helper.getSortValue(a.name, b.name);
        });
        this.ac?.setTranslocoService(this.transloco);
      },
      error: (error: any) => {},
    }).add(() => {
      this.loading = false;
    });
    this.initFormBehaviour();
    this.ui.setFocus();
    this.country = this.ac.country;
    this.timezone = this.ac.timezone;
  }

  onSubmit(): void {
    this.ui.hideMessage();
    if (this.form.valid === true) {
      this.submitting = true;
      const payload = {
        name: this.form.value.name,
        language: this.form.value.language,
        location_type: this.form.value.location_type,
        location_name: this.form.value.location_name,
        country: this.country,
        timezone: this.timezone,
        conference_url: this.form.value.conference_url,
        conference_info: this.form.value.conference_info,
        informations_internal: this.form.value.informations_internal,
        informations_moderator: this.form.value.informations_moderator,
        documents_visible_observer: this.form.value.documents_visible_observer,
        customer: this.form.value.customer?.id || null,
        dispatcher: this.form.value.dispatcher.id,
        ratingtype: this.form.value.ratingtype.id,
        start_time: moment(this.form.value.start_time).format('YYYY-MM-DD HH:mm:ss'),
      };
      this.api.saveModel('acs', this.ac.id, payload).subscribe({
        next: (result: any) => {
          this.ui.toastSuccess(this.transloco.translate('common.messages.saved', { value: this.form.value.name }), this.transloco.translate('common.saved'));
          this.ac.deserialize(result.data);
          this.stopEdit();
        },
        error: (error: any) => {
          this.ui.showMessage('error', this.transloco.translate(error.message), this.transloco.translate('common.error'));
          FormHelper.setBackendErrors(this.form, error.fielderrors);
        },
      }).add(() => {
        this.submitting = false;
      });
    } else {
      this.ui.showMessage('error', this.transloco.translate('common.error_on_form_please_correct'), this.transloco.translate('common.error'));
      FormHelper.markFormAsDirty(this.form);
    }
  }

  onEdit(): void {
    if (this.auth.hasRight('admin.ac.edit')) {
      this.startEdit();
    }
  }

  startEdit(): void {
    if (this.ac !== null) {
      this.form.patchValue({
        name: this.ac.name,
        language: this.ac.language,
        customer: this.customers.find(customer => customer.id === this.ac.customer?.id),
        dispatcher: this.dispatchers.find(dispatcher => dispatcher.id === this.ac.dispatcher?.id),
        ratingtype: this.ratingtypes.find(ratingtype => ratingtype.id === this.ac.ratingtype.id),
        location_type: (this.ac.onsite === true) ? 'onsite' : 'virtual',
        location_name: this.ac.location_name,
        conference_url: this.ac.conference_url,
        conference_info: this.ac.conference_info,
        informations_internal: this.ac.informations_internal,
        informations_moderator: this.ac.informations_moderator,
        documents_visible_observer: this.ac.documents_visible_observer,
        start_time: (this.ac.start_time_admin) ? moment(this.ac.start_time_admin).toDate() : null,
      });
    }
    this.inEdit = true;
    this.setEdit.emit('basedata');
    this.ui.setFormFocus();
  }

  stopEdit(): void {
    this.form.reset();
    this.country = this.ac.country;
    this.timezone = this.ac.timezone;
    this.inEdit = false;
    this.setEdit.emit(null);
    this.ui.setFocus();
  }

  onDeleteAc(): void {
    if (this.auth.hasRight('admin.ac.delete')) {
      this.confirmationService.confirm({
        message: this.transloco.translate('common.messages.really_delete', { value: this.ac.name }),
        header: this.transloco.translate('common.really_delete'),
        icon: 'pi pi-exclamation-triangle',
        accept: () => {
          this.api.deleteModel('acs', this.ac.id).subscribe({
            next: () => {
              this.ui.toastSuccess(this.transloco.translate('common.messages.deleted', { value: this.ac.name }), this.transloco.translate('common.deleted'));
              this.router.navigate(['/acs', this.mode]);
            },
            error: (error: any) => {
              this.ui.toastError(this.transloco.translate(error.message));
            }
          });
        }
      });
    }
  }

  onCopyAc(): void {
    if (this.auth.hasRight('admin.ac.create')) {
      this.confirmationService.confirm({
        message: this.transloco.translate('ac_basedata.really_copy', { value: this.ac.name }),
        header: this.transloco.translate('ac_basedata.copy'),
        icon: 'pi pi-exclamation-triangle',
        accept: () => {
          this.api.copyAc(this.ac.id).subscribe({
            next: (result) => {
              this.ui.toastSuccess(this.transloco.translate('ac_basedata.copied', { value: this.ac.name }), this.transloco.translate('common.copied'));
              this.ac = new AcModel().deserialize(result.data);
              this.router.navigate(['/acs/planned', result.data.id]);
              this.buildMenu();
            },
            error: (error: any) => {
              this.ui.toastError(this.transloco.translate(error.message));
            }
          });
        }
      });
    }
  }

  onCancelAc(): void {
    if (this.auth.hasRight('admin.ac.edit')) {
      this.confirmationService.confirm({
        message: this.transloco.translate('ac_basedata.really_cancel', { value: this.ac.name }),
        header: this.transloco.translate('ac_basedata.cancel'),
        icon: 'pi pi-exclamation-triangle',
        accept: () => {
          this.api.cancelAc(this.ac.id).subscribe({
            next: (result) => {
              this.ac = new AcModel().deserialize(result.data);
              this.ui.toastSuccess(this.transloco.translate('ac_basedata.cancelled', { value: this.ac.name }), this.transloco.translate('common.cancelled'));
              this.router.navigate(['/acs/cancelled', result.data.id]);
              this.buildMenu();
            },
            error: (error: any) => {
              this.ui.toastError(this.transloco.translate(error.message));
            }
          });
        }
      });
    }
  }

  getDstLabel(): string {
    return ' (' + this.helper.getTimezoneName(this.timezone, this.form.get('start_time').value) + ')';
  }

  private buildMenu(): void {
    if (this.ac !== null) {
      this.items = [];
      if (this.auth.hasRight('admin.ac.create')) {
        this.items.push({
          label: this.transloco.translate('common.labels.copy'),
          icon: 'pi pi-copy',
          command: () => {
            this.onCopyAc();
          }
        });
      }
      if (this.auth.hasRight('admin.ac.edit') && this.ac.isCancellable()) {
        this.items.push({
          label: this.transloco.translate('common.labels.cancel2'),
          icon: 'pi pi-ban',
          command: () => {
            this.onCancelAc();
          }
        });
      }
      if (this.auth.hasRight('admin.ac.delete')) {
        this.items.push({
          label: this.transloco.translate('common.labels.delete'),
          icon: 'pi pi-trash',
          styleClass: 'danger-button',
          disabled: (!this.ac.deletable),
          command: () => {
            this.onDeleteAc();
          }
        });
      }
    }
  }

  private initFormBehaviour(): void {
    this.form.get('location_type').valueChanges.subscribe((value: string) => {
      if (value === 'onsite') {
        this.form.get('location_name').enable();
        this.form.get('conference_url').disable();
        this.form.get('conference_info').disable();
        this.form.get('location_name').setValidators([Validators.required]);
      } else {
        this.form.get('location_name').disable();
        this.form.get('conference_url').enable();
        this.form.get('conference_info').enable();
        this.form.get('location_name').setValue('');
        this.form.get('location_name').clearValidators();
      }
      this.form.get('location_name').updateValueAndValidity();
    });
  }

}
